/**
 * WEB-1524: адрес проекта для ДЕНЕЖНЫХ действий раздела «Баланс/Тариф».
 *
 * Оба штатных входа на страницу (кошелёк шапки, «Пополнить» в полосе
 * отрицательного баланса) диспатчат clearActiveProjectThunk → currentProject
 * становится null, а серверные денежные ручки project-keyed
 * (POST /billing/checkout, /billing/portal, /billing/credits/purchase требуют
 * projectId; RBAC там — isProjectOrgManager, WEB-283.4). Орг-адресации у этих
 * ручек нет, поэтому мост: без выбранного проекта подставляем первый
 * доступный проект АКТИВНОЙ организации.
 *
 * Деньги при этом всё равно орг-уровневые (кошелёк и подписка на организации,
 * WEB-283/ADR-0020) — проект здесь лишь адресный ключ, любой проект орги
 * резолвится в ту же организацию.
 *
 * Фильтр по activeOrgId обязателен: сразу после переключения организации
 * список проектов может отставать (стейл-проекты ЧУЖОЙ орги) — подставить
 * такой проект значило бы провести оплату не той организации (сценарий
 * приёмки WEB-1524 №8). Проекты без orgId (легаси-объекты, загруженные до
 * WEB-1417) принимаются: сам список грузится уже скоупленным по активной
 * орге (fetchProjectsThunk(activeOrgId), App.tsx).
 *
 * ── WEB-1648: мост УЗАКОНЕН, это не недоделка ────────────────────────────
 *
 * Решение владельца 02.09 (вариант Б): адресация проектом — НАМЕРЕННАЯ
 * постоянная модель, а не временная заплатка. Основание: деньги считаются
 * верно (кошелёк и подписка живут на организации), а переделка денежного
 * пути ради архитектурной чистоты — плохой размен на работающем контуре.
 * Читатель, которому это показалось долгом: карточка уже заведена, закрыта
 * и превращена в модель — заводить её заново не нужно.
 *
 * Целевое состояние — всё же орг-адресация (вариант А). Условие возврата к
 * ней записано ДОСЛОВНО (владелец, 02.09) — любое из:
 *   · появляется денежная операция, которую НЕВОЗМОЖНО осмысленно
 *     адресовать проектом (например, что-то на уровне организации целиком);
 *   · подставщик даёт первый живой отказ — то есть выбирает не тот проект
 *     и это видит клиент;
 *   · заходит работа по денежному контуру организации, к которой перевод
 *     пристёгивается почти бесплатно.
 * Пока ни одно не наступило — мост стоит.
 *
 * Сторож на само правило: `__tests__/useBillingProjectId.web1648.test.tsx`
 * (правило подстановки + три крайних случая). Сторож на второй конец —
 * «каждая денежная поверхность кабинета берёт адрес отсюда, а каждая
 * денежная ручка сервера его требует» — `backend/src/shared/guards/
 * billing-project-key-ends.spec.ts` (строка в two-ended-rules.inventory.md).
 */
import { useAppSelector } from '@/hooks/hooks';

/*
 * WEB-1648 (условие узаконивания №3): подставлять нечего — денежная кнопка
 * ПОГАШЕНА И НАЗЫВАЕТ ПРИЧИНУ. Ни отказа сервера, ни пустого запроса, но и
 * не молчаливо мёртвая кнопка. Текст по-прежнему ОДИН на все денежные
 * поверхности — WEB-1874 перенёс его в словарь денег
 * (`i18n/dictionaries/billing.ts`, ключ `noBillingProjectHint`), потому что
 * копия текста в модуле не умеет говорить на языке кабинета. Место одно, как
 * и было; сменился только адрес.
 */

export function useBillingProjectId(): string | undefined {
  const currentId = useAppSelector(
    (state) => state.projects?.currentProject?.id,
  );
  const projects = useAppSelector((state) => state.projects?.projects ?? []);
  const activeOrgId = useAppSelector(
    (state) => state.organizations?.activeOrgId ?? null,
  );

  if (currentId) return currentId;

  const candidate = projects.find(
    (p) => !activeOrgId || !p.orgId || p.orgId === activeOrgId,
  );
  return candidate?.id;
}
